// JOGI KAPU — a blokkoló ablak és az elutasítási képernyő.
// Terv és szövegek: ClickUp 123qmzqzk1r §10 (J8, J9), §8.
//
// ⚠️ A MODÁL-REGRESSZIÓ ELLENŐRZŐLISTÁJA (terv §15) ITT VAN BETARTVA, mert a
// „modál görgetés/túlcsordulás" hibacsalád KÉTSZER ütött már, és ez a modál
// HOSSZABB bármelyik eddiginél:
//   1. max-height dvh-ban + belső overflow-y  → a tartalom görgethető, nem vág
//   2. max-width 100vw + box-sizing: border-box → nincs vízszintes túlcsordulás
//   3. z-index az ALSÓ NAVIGÁCIÓ FELETT       → a nav és az AiChat z-50, ez z-[60]
//   4. a háttér-görgetés zárolva, amíg a modál áll
// A `dvh` a `vh` helyett szándékos: iOS Safariban a `vh` a cím- és eszköztárat
// is beleszámolja, ezért a modál alja a kijelző alá csúszik.
//
// ⚠️ 2. szabály: nincs egészségügyi tartalom, nincs adagolásra utalás, és
// nincs riasztás-szemantikájú szín (se piros, se narancs) — ez egy jogi
// tájékoztató, nem figyelmeztetés.
//
// ⚠️ A dátum MINDENHOL a config `effectiveFrom`-jából renderelődik (J8).
// Kézi dátum a szövegekben NINCS — ezt teszt is védi.

import { useEffect, useState } from "react";
import { openExternal } from "../utils/openExternal";
import { formatLegalDate } from "../utils/legalDate";
import type { LegalGateItem } from "../utils/legalGate";

interface Props {
  items: LegalGateItem[];
  /** Nyugtázás — a hívó írja a Firestore-ba. Hibát dobhat. */
  onAccept: (items: LegalGateItem[]) => Promise<void>;
  /** „Nem fogadom el" — csak akkor látszik, ha van `accept` fajtájú tétel. */
  onReject: () => void;
}

const CIM_EGY = "Frissítettük a jogi dokumentumunkat";
const CIM_KETTO = "Frissítettük a jogi dokumentumainkat";

const DOK_NEV: Record<string, string> = {
  privacy: "Adatvédelmi tájékoztató",
  terms: "Felhasználási feltételek",
};

/** A nyugtázó jelölő szövege — a kapu fajtája szerint. */
const JELOLO: Record<string, string> = {
  acknowledge: "Tudomásul veszem az adatvédelmi tájékoztató módosítását.",
  accept: "Elfogadom a módosított felhasználási feltételeket.",
};

/** A jelölő alatti magyarázat — a tudomásulvétel NEM hozzájárulás. */
const JELOLO_ALA: Record<string, string> = {
  acknowledge: "Ez nem hozzájárulás, hanem annak a nyoma, hogy tájékoztattunk.",
  accept: "Elfogadás nélkül az alkalmazás nem használható tovább.",
};

/** A háttér-görgetés zárolása, amíg a modál áll. */
function useScrollLock() {
  useEffect(() => {
    const elozo = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = elozo;
    };
  }, []);
}

export function LegalGateModal({ items, onAccept, onReject }: Props) {
  const [jelolt, setJelolt] = useState<Record<string, boolean>>({});
  const [kuldes, setKuldes] = useState(false);
  const [hiba, setHiba] = useState<string | null>(null);
  useScrollLock();

  if (items.length === 0) return null;

  // Minden tételnél KÜLÖN jelölő kell — egy "mindent elfogadok" pipa nem
  // tudná igazolni, hogy a felhasználó mindkét dokumentumról tudott.
  const mindJelolt = items.every((i) => jelolt[i.docType]);
  // Tudomásulvételnél nincs elutasítás-ág: a tájékoztatás nem hozzájárulás
  // (§8). Az elutasítás gomb csak akkor jelenik meg, ha van `accept` tétel.
  const vanElfogadas = items.some((i) => i.gate === "accept");
  const hatalybalepes = formatLegalDate(items[0].effectiveFrom);

  async function kuld() {
    setHiba(null);
    setKuldes(true);
    try {
      await onAccept(items);
    } catch {
      // A hívó dönt a naplózásról; itt a felhasználónak kell válasz.
      setHiba("A mentés nem sikerült. Ellenőrizd az internetkapcsolatot, és próbáld újra.");
      setKuldes(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="jogi-kapu-cim"
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col"
        style={{
          maxWidth: "100vw",
          boxSizing: "border-box",
          maxHeight: "min(92dvh, 92vh)",
        }}
      >
        <div className="px-5 pt-5 pb-3 border-b border-gray-100">
          <h2 id="jogi-kapu-cim" className="text-lg font-semibold text-gray-800">
            {items.length > 1 ? CIM_KETTO : CIM_EGY}
          </h2>
          {hatalybalepes && (
            <p className="text-sm text-gray-500 mt-1">
              {hatalybalepes}-tól új változat van érvényben. Kérünk, olvasd el, mi változott.
            </p>
          )}
        </div>

        {/* A görgethető törzs — ez a pont, ahol a hibacsalád kétszer ütött. */}
        <div className="px-5 py-4 overflow-y-auto space-y-5" style={{ WebkitOverflowScrolling: "touch" }}>
          {items.map((item) => (
            <section key={item.docType} className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-800">
                {DOK_NEV[item.docType] || item.docType}
                {formatLegalDate(item.version) ? ` · ${formatLegalDate(item.version)}-i változat` : ""}
              </h3>

              {/* A „mi változott" a MODÁLBAN van, nem csak a linken túl:
                  natívon a jogi lap a WEBRŐL nyílik (openExternal), tehát
                  offline nem érhető el — a nyugtázás viszont nem függhet a
                  link megnyitásától. */}
              {item.summary && (
                <p className="text-sm text-gray-600 leading-snug whitespace-pre-line">{item.summary}</p>
              )}

              {item.url && (
                <button
                  type="button"
                  onClick={() => openExternal(item.url)}
                  className="text-sm text-emerald-700 underline underline-offset-2"
                >
                  Teljes szöveg megnyitása
                </button>
              )}

              <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  id={`jogi-jelolo-${item.docType}`}
                  checked={!!jelolt[item.docType]}
                  onChange={(e) =>
                    setJelolt((p) => ({ ...p, [item.docType]: e.target.checked }))
                  }
                  className="mt-0.5 w-4 h-4 accent-emerald-600 shrink-0"
                />
                <span className="text-sm text-gray-700 font-medium">
                  {JELOLO[item.gate]}
                </span>
              </label>
              <p className="text-xs text-gray-400 pl-7 leading-snug">{JELOLO_ALA[item.gate]}</p>
            </section>
          ))}

          {hiba && (
            <p className="text-sm text-gray-700 bg-gray-100 border border-gray-200 rounded-xl px-3 py-2">
              {hiba}
            </p>
          )}
        </div>

        <div
          className="px-5 pt-3 border-t border-gray-100 flex flex-col gap-2"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }}
        >
          <button
            type="button"
            onClick={kuld}
            disabled={!mindJelolt || kuldes}
            className="w-full bg-emerald-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {kuldes ? (
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              "Tovább az alkalmazáshoz"
            )}
          </button>
          {vanElfogadas && (
            <button
              type="button"
              onClick={onReject}
              disabled={kuldes}
              className="w-full border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              Nem fogadom el
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface RejectedProps {
  effectiveFrom: string;
  /** „Adataim exportálása" és „Fiókom törlése" — NAVIGÁL a /profil-ra. */
  onProfil: () => void;
  onLogout: () => void;
  /** „Mégis elfogadom" — vissza a modálhoz. */
  onBack: () => void;
}

/**
 * Elutasítási képernyő (J9). NÉGY gomb, és kettő közülük NAVIGÁL, nem
 * újraimplementál: az export és a fióktörlés a `Profil.tsx`-ben él
 * beágyazva, közös util nélkül — duplikálni őket a 10. szabályt sértené
 * (§8). Ezért a `/profil` az EGYETLEN whitelistelt útvonal a kapu mögött.
 */
export function LegalRejectedScreen({ effectiveFrom, onProfil, onLogout, onBack }: RejectedProps) {
  const hatalybalepes = formatLegalDate(effectiveFrom);
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-sm border border-gray-100 p-5 space-y-4"
           style={{ maxWidth: "100vw", boxSizing: "border-box" }}>
        <h1 className="text-lg font-semibold text-gray-800">Nem fogadtad el a feltételeket</h1>
        <p className="text-sm text-gray-600 leading-snug">
          {hatalybalepes ? `${hatalybalepes}-tól ` : ""}a szolgáltatás a módosított feltételek
          elfogadásával használható. Az adataidhoz továbbra is hozzáférsz.
        </p>
        <div className="flex flex-col gap-2 pt-1">
          <button
            type="button"
            onClick={onBack}
            className="w-full bg-emerald-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors"
          >
            Mégis elfogadom
          </button>
          <button
            type="button"
            onClick={onProfil}
            className="w-full border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors"
          >
            Adataim exportálása
          </button>
          <button
            type="button"
            onClick={onProfil}
            className="w-full border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors"
          >
            Fiókom törlése
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="w-full text-gray-500 py-2 text-sm font-medium hover:text-gray-700 transition-colors"
          >
            Kijelentkezés
          </button>
        </div>
      </div>
    </div>
  );
}
